import React, { useState, useEffect, useContext, useRef } from 'react';
import Layout from '../components/Layout';
import { AuthContext } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { UploadCloud, FileVideo, Trash2, Download, PlayCircle, X, Image as ImageIcon, Archive, FileText, File, Smartphone } from 'lucide-react';
import { normalizeRole } from '../utils/roleUtils';

const FileSharing = () => {
    const { user } = useContext(AuthContext);
    const normalizedRole = normalizeRole(user?.role);
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);

    // Yetki Kontrolü: admin, boss, staff girebilir
    const isAllowed = ['admin', 'boss', 'staff'].includes(normalizedRole);

    useEffect(() => {
        if (isAllowed) {
            fetchFiles();
        }
    }, [isAllowed]);

    useEffect(() => () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
    }, [previewUrl]);

    if (!isAllowed) {
        return <Navigate to="/dashboard" />;
    }

    const fetchFiles = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/files');
            setFiles(res.data);
        } catch (error) {
            console.error("Dosyalar alınamadı", error);
            // Sadece hata yetkisizlikten değilse göster
            if (error.response?.status !== 403) {
                toast.error('Dosya listesi yüklenemedi.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 512 * 1024 * 1024) {
            toast.error('Dosya boyutu 512 MB\'dan büyük olamaz.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        setUploadProgress(0);

        try {
            await api.post('/files/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });
            toast.success('Dosya başarıyla yüklendi!');
            fetchFiles(); // Listeyi yenile
        } catch (error) {
            toast.error(error.response?.data?.message || 'Dosya yüklenemedi.');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) {
            try {
                await api.delete(`/files/${id}`);
                toast.success('Dosya silindi.');
                fetchFiles();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Silme işlemi başarısız.');
            }
        }
    };

    const resetPreview = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl('');
        setSelectedFile(null);
        setIsPreviewLoading(false);
    };

    const handleDownload = async (file) => {
        try {
            const response = await api.get(`/files/${file.id}/download`, { responseType: 'blob' });
            const blobUrl = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.original_name;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Dosya indirilemedi.');
        }
    };

    const openPreview = async (file) => {
        const isPreviewable = file.file_type?.startsWith('video/') || file.file_type?.startsWith('image/');
        if (!isPreviewable) {
            return;
        }

        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl('');
        }

        setSelectedFile(file);
        setIsPreviewLoading(true);

        try {
            const response = await api.get(`/files/${file.id}/content`, { responseType: 'blob' });
            setPreviewUrl(URL.createObjectURL(response.data));
        } catch (error) {
            setSelectedFile(null);
            toast.error(error.response?.data?.message || 'Dosya onizlemesi yuklenemedi.');
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    // Dosya türüne göre ikon ve renk belirle
    const getFileTypeInfo = (fileType) => {
        if (!fileType) return { icon: <File size={48} />, color: 'text-slate-500', label: 'Dosya', bgIcon: <File size={100} /> };
        if (fileType.startsWith('video/')) return { icon: <PlayCircle size={48} />, color: 'text-blue-400', label: 'Video', bgIcon: <FileVideo size={100} /> };
        if (fileType.startsWith('image/')) return { icon: <ImageIcon size={48} />, color: 'text-green-400', label: 'Resim', bgIcon: <ImageIcon size={100} /> };
        if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('7z') || fileType.includes('tar') || fileType.includes('gz') || fileType.includes('compressed')) return { icon: <Archive size={48} />, color: 'text-yellow-400', label: 'Arşiv', bgIcon: <Archive size={100} /> };
        if (fileType.includes('pdf') || fileType.includes('document') || fileType.includes('text') || fileType.includes('word') || fileType.includes('spreadsheet') || fileType.includes('excel')) return { icon: <FileText size={48} />, color: 'text-orange-400', label: 'Belge', bgIcon: <FileText size={100} /> };
        return { icon: <File size={48} />, color: 'text-slate-400', label: 'Dosya', bgIcon: <File size={100} /> };
    };

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-white mb-2">Dosya Paylaşımı</h1>
                    <p className="text-slate-400 text-sm md:text-base">Video, fotoğraf, belge, arşiv ve diğer dosyalarınızı kalite kaybı olmadan paylaşın.</p>
                </div>

                {/* Yükle Butonu */}
                <div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        accept="*/*"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-6 py-3 rounded-xl shadow-lg transition-all font-bold disabled:opacity-50"
                    >
                        {uploading ? (
                            <span className="flex items-center gap-2">
                                <span className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full"></span>
                                Yükleniyor %{uploadProgress}
                            </span>
                        ) : (
                            <>
                                <UploadCloud size={20} />
                                Dosya Yükle
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* ANDROID UYGULAMASI İNDİRME */}
            <a
                href={`${api.defaults.baseURL}/download/app`}
                className="glass rounded-2xl border border-green-500/30 p-4 md:p-5 mb-8 flex items-center justify-between gap-4 hover:border-green-500/60 transition-all hover:-translate-y-0.5 shadow-md group"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-green-500/10 rounded-xl border border-green-500/20">
                        <Smartphone size={24} className="text-green-400" />
                    </div>
                    <div>
                        <h3 className="font-bold text-white">Android Uygulaması (Saha Personeli)</h3>
                        <p className="text-xs text-slate-400 mt-0.5">Güncel APK'yı indirip telefona kurun; mevcut uygulamanın üstüne güncellenir.</p>
                    </div>
                </div>
                <span className="flex items-center gap-2 bg-green-600 group-hover:bg-green-500 text-white px-4 py-2.5 rounded-xl font-bold text-sm shrink-0 transition-colors">
                    <Download size={16} />
                    APK İNDİR
                </span>
            </a>

            {/* Dosyalar Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {isLoading ? (
                    <div className="col-span-full text-center py-10 text-slate-400">Dosyalar yükleniyor...</div>
                ) : files.length === 0 ? (
                    <div className="col-span-full border border-dashed border-slate-700/50 rounded-2xl p-12 flex flex-col items-center justify-center text-center glass">
                        <UploadCloud size={48} className="text-slate-500 mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">Henüz Dosya Yüklenmemiş</h3>
                        <p className="text-slate-400">Dosya eklemek için sağ üstteki butonu kullanın.</p>
                    </div>
                ) : (
                    files.map(file => {
                        const typeInfo = getFileTypeInfo(file.file_type);

                        return (
                            <div key={file.id} className="glass border border-slate-700/50 rounded-2xl overflow-hidden hover:border-primary-500/50 transition-colors group flex flex-col">
                                {/* Thumbnail / Icon Alanı */}
                                <div
                                    className="h-40 bg-slate-800/80 relative flex items-center justify-center cursor-pointer overflow-hidden"
                                    onClick={() => openPreview(file)}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60 z-10"></div>

                                    <>
                                        <div className={`${typeInfo.color} group-hover:scale-110 transition-all z-20`}>
                                            {typeInfo.icon}
                                        </div>
                                        <div className="absolute text-slate-700 opacity-20">
                                            {typeInfo.bgIcon}
                                        </div>
                                    </>

                                    {/* Tür etiketi */}
                                    <span className={`absolute top-3 left-3 z-20 px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-900/70 ${typeInfo.color}`}>
                                        {typeInfo.label}
                                    </span>
                                </div>

                                {/* İçerik Bilgisi */}
                                <div className="p-4 flex-1 flex flex-col">
                                    <h3 className="text-white font-bold truncate mb-1" title={file.original_name}>
                                        {file.original_name}
                                    </h3>
                                    <div className="flex justify-between items-center text-xs text-slate-400 mb-4">
                                        <span>{file.uploader_name}</span>
                                        <span>{formatBytes(file.file_size)}</span>
                                    </div>

                                    <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-700/50">
                                        <span className="text-xs text-slate-500">
                                            {new Date(file.createdAt).toLocaleDateString('tr-TR')}
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleDownload(file)}
                                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                                                title="Indir"
                                            >
                                                <Download size={18} />
                                            </button>
                                            {/* SİLME BUTONU: SADECE ADMİN */}
                                            {user?.role === 'admin' && (
                                                <button
                                                    onClick={() => handleDelete(file.id)}
                                                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    title="Sil"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Dosya İzleme / Önizleme Modalı */}
            {selectedFile && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/95 backdrop-blur-sm">
                    <button
                        onClick={resetPreview}
                        className="absolute top-4 right-4 md:top-6 md:right-6 p-2 text-white/50 hover:text-white bg-slate-800 rounded-full transition-colors z-10"
                    >
                        <X size={24} />
                    </button>

                    <div className="w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col">
                        <div className="p-3 md:p-4 bg-slate-800 flex justify-between items-center border-b border-slate-700">
                            <h3 className="text-white font-bold truncate pr-4 text-sm md:text-base">{selectedFile.original_name}</h3>
                            <button
                                onClick={() => handleDownload(selectedFile)}
                                className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 font-medium shrink-0"
                            >
                                <Download size={16} /> İndir
                            </button>
                        </div>
                        {isPreviewLoading ? (
                            <div className="flex items-center justify-center p-10 text-slate-300">Onizleme yukleniyor...</div>
                        ) : selectedFile.file_type?.startsWith('video/') && previewUrl ? (
                            <video src={previewUrl} controls autoPlay className="w-full max-h-[75vh] object-contain" />
                        ) : selectedFile.file_type?.startsWith('image/') && previewUrl ? (
                            <img src={previewUrl} alt={selectedFile.original_name} className="w-full max-h-[75vh] object-contain" />
                        ) : (
                            <div className="flex items-center justify-center p-10 text-slate-400">Bu dosya turu icin onizleme kullanilamiyor.</div>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default FileSharing;
